"use strict";
/**
 * dispose.js provides tools to objects that needs to dispose resources, such as destroy DOM, and
 * unsubscribe from events. The motivation with examples is presented here:
 *
 *    https://phab.getgrist.com/w/disposal/
 *
 * Disposable is a class for components that need cleanup (e.g. maintain DOM, listen to events,
 * subscribe to anything). It provides a .dispose() method that should be called to destroy the
 * component, and .onDispose()/.autoDispose() methods that the component should use to take
 * responsibility for other pieces that require cleanup.
 *
 * To define a disposable class:
 *    class Foo extends Disposable { ... }
 *
 * To create Foo:
 *    const foo = Foo.create(owner, ...args);
 * This is better than `new Foo` for two reasons:
 *    1. If Foo's constructor throws an exception, any disposals registered in that constructor
 *       before the exception are honored.
 *    2. It ensures you specify the owner of the new instance (but you can use null to skip it).
 *
 * In Foo's constructor (or rarely methods), take ownership of other Disposable objects:
 *    this.bar = Bar.create(this, ...);
 *
 * For objects that are not instances of Disposable but have a .dispose() methods, use:
 *    this.bar = this.autoDispose(createSomethingDisposable());
 *
 * To call a function on disposal (e.g. to add custom disposal logic):
 *    this.onDispose(() => this.myUnsubscribeAllMethod());
 *    this.onDispose(this.myUnsubscribeAllMethod, this);    // slightly more efficient
 *
 * To mark this object to be wiped out on disposal (i.e. set all properties to null):
 *    this.wipeOnDispose();
 * See the documentation of that method for more info.
 *
 * To dispose Foo directly:
 *    foo.dispose();
 * To determine if an object has already been disposed:
 *    foo.isDisposed()
 *
 * If you need to replace an owned object, or release, or dispose it early, use a Holder:
 *    this._holder = Holder.create(this);
 *    Bar.create(this._holder, 1);      // creates new Bar(1)
 *    Bar.create(this._holder, 2);      // creates new Bar(2) and disposes previous object
 *    this._holder.clear();             // disposes contained object
 *    this._holder.release();           // releases contained object
 *
 * If you need a container for multiple objects and dispose them all together, use a MultiHolder:
 *    this._mholder = MultiHolder.create(null);
 *    Bar.create(this._mholder, 1);     // create new Bar(1)
 *    Bar.create(this._mholder, 2);     // create new Bar(2)
 *    this._mholder.dispose();          // disposes both objects
 *
 * If creating your own class with a dispose() method, do NOT throw exceptions from dispose().
 * These cannot be handled properly in all cases. Read here about the same issue in C++:
 *    http://bin-login.name/ftp/pub/docs/programming_languages/cpp/cffective_cpp/MAGAZINE/SU_FRAME.HTM#destruct
 *
 * Using a parametrized (generic) class as a Disposable is tricky. E.g.
 *    class Bar<T> extends Disposable { ... }
 *    // Bar<T>.create(...)   <-- doesn't work
 *    // Bar.create<T>(...)   <-- doesn't work
 *    // Bar.create(...)      <-- works, but with {} for Bar's type parameters
 *
 * The solution is to expose the constructor type using a helper method:
 *    class Bar<T> extends Disposable {
 *      // Note the tuple below which must match the constructor parameters of Bar<U>.
 *      public static ctor<U>(): IDisposableCtor<Bar<U>, [U, boolean]> { return this; }
 *      constructor(a: T, b: boolean) { ... }
 *    }
 *    Bar.ctor<T>().create(...)   // <-- works, creates Bar<T>, and does type-checking!
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setDisposeOwner = exports.MultiHolder = exports.Holder = exports.Disposable = void 0;
const emit_1 = require("./emit");
// Internal "owner" of disposable objects which doesn't actually dispose or keep track of them. It
// is the effective owner when creating a Disposable with `new Foo()` rather than `Foo.create()`.
const _noopOwner = {
    autoDispose(obj) { },
};
// Newly-created Disposable instances will have this as their owner. This is not a constant, it
// is used by create() for the safe creation of Disposables.
let _defaultDisposableOwner = _noopOwner;
/**
 * Base class for disposable objects that can own other objects. See the module documentation.
 */
class Disposable {
    constructor() {
        this._disposalList = new DisposalList();
        // This registers with a temp Holder when using create(), and is a no-op when using `new Foo`.
        _defaultDisposableOwner.autoDispose(this);
        // Be sure to reset to no-op, so that a (non-recommended) direct call like 'new Bar()', from
        // inside Foo's constructor doesn't use the same Holder that's temporarily holding Foo.
        _defaultDisposableOwner = _noopOwner;
    }
    /**
     * Create Disposable instances using `Class.create(owner, ...)` rather than `new Class(...)`.
     *
     * This reminds you to provide an owner, and ensures that if the constructor throws an
     * exception, dispose() gets called to clean up the partially-constructed object.
     *
     * Owner may be null if intend to ensure disposal some other way.
     */
    static create(owner, ...args) {
        const origDefaultOwner = _defaultDisposableOwner;
        const holder = new Holder();
        try {
            // The newly-created object will have holder as its owner.
            _defaultDisposableOwner = holder;
            return setDisposeOwner(owner, new this(...args));
        }
        catch (e) {
            try {
                // This calls dispose on the partially-constructed object
                holder.clear();
            }
            catch (e2) {
                // tslint:disable-next-line:no-console
                console.error("Error disposing partially constructed %s:", this.name, e2);
            }
            throw e;
        }
        finally {
            // On success, the new object has a new owner, and we release it from holder.
            // On error, the holder has been cleared, and the release() is a no-op.
            holder.release();
            _defaultDisposableOwner = origDefaultOwner;
        }
    }
    /** Take ownership of obj, and dispose it when this.dispose() is called. */
    autoDispose(obj) {
        this.onDispose(obj.dispose, obj);
        return obj;
    }
    /** Call the given callback when this.dispose() is called. */
    onDispose(callback, context) {
        return this._disposalList.addListener(callback, context);
    }
    /**
     * Wipe out this object when it is disposed, i.e. set all its properties to null. It is
     * recommended to call this early in the constructor.
     *
     * This makes disposal more costly, but has certain benefits:
     * - If anything still refers to the object and uses it, we'll get an early error, rather than
     *   silently keep going, potentially doing useless work (or worse) and wasting resources.
     * - If anything still refers to the object (even without using it), the fields of the object
     *   can still be garbage-collected.
     * - If there are circular references involving this object, they get broken, making the job
     *   easier for the garbage collector.
     *
     * The recommendation is to use it for complex, longer-lived objects, but to skip for objects
     * which are numerous and short-lived (and less likely to be referenced from unexpected places).
     */
    wipeOnDispose() {
        this.onDispose(this._wipeOutObject, this);
    }
    /**
     * Returns whether this object has already been disposed.
     */
    isDisposed() {
        return this._disposalList === null;
    }
    /**
     * Clean up `this` by disposing all owned objects, and calling onDispose() callbacks, in reverse
     * order to that in which they were added.
     */
    dispose() {
        const disposalList = this._disposalList;
        if (!disposalList) {
            // tslint:disable-next-line:no-console
            console.error("Error disposing %s which is already disposed", _describe(this));
        }
        else {
            this._disposalList = null;
            disposalList.callAndDispose(this);
        }
    }
    /**
     * Wipe out this object by setting each property to null. This is helpful for objects that are
     * disposed and should be ready to be garbage-collected.
     */
    _wipeOutObject() {
        // The sentinel value doesn't have to be null, but some values cause more helpful errors than
        // others. E.g. if a.x = "disposed", then a.x.foo() throws "undefined is not a function", but
        // when a.x = null, a.x.foo() throws a more helpful "Cannot read property 'foo' of null".
        for (const k of Object.keys(this)) {
            this[k] = null;
        }
    }
}
exports.Disposable = Disposable;
/**
 * Holder keeps a single disposable object. If given responsibility for another object using
 * holder.autoDispose() or Foo.create(holder, ...), it automatically disposes the currently held
 * object. It also disposes it when the holder itself is disposed.
 *
 * If the object is an instance of Disposable, the holder will also notice when the object gets
 * disposed from outside of it, in which case the holder will become empty again.
 */
class Holder {
    constructor() {
        this._owned = null;
        this._disposalListener = undefined;
    }
    static create(owner) {
        return setDisposeOwner(owner, new Holder());
    }
    /** Take ownership of a new object, disposing the previously held one. */
    autoDispose(obj) {
        this.clear();
        this._owned = obj;
        if (obj instanceof Disposable) {
            this._disposalListener = obj.onDispose(this._onOutsideDispose, this);
        }
        return obj;
    }
    /** Releases the held object without disposing it, emptying the holder. */
    release() {
        this._unlisten();
        const ret = this._owned;
        this._owned = null;
        return ret;
    }
    /** Disposes the held object and empties the holder. */
    clear() {
        this._unlisten();
        const owned = this._owned;
        if (owned) {
            this._owned = null;
            owned.dispose();
        }
    }
    /** Returns the held object, or null if the Holder is empty. */
    get() { return this._owned; }
    /** Returns whether the Holder is empty. */
    isEmpty() { return !this._owned; }
    /** When the holder is disposed, it disposes the held object if any. */
    dispose() { this.clear(); }
    /** Stop listening for the disposal of this._owned. */
    _unlisten() {
        const disposalListener = this._disposalListener;
        if (disposalListener) {
            this._disposalListener = undefined;
            disposalListener.dispose();
        }
    }
    _onOutsideDispose() {
        this._disposalListener = undefined;
        this._owned = null;
    }
}
exports.Holder = Holder;
/**
 * MultiHolder keeps multiple disposable object. It disposes all held object when the holder
 * itself is disposed. It's actually nothing more than the Disposable base class itself, just
 * exposed with a clearer name that describes its purpose.
 */
class MultiHolder extends Disposable {
}
exports.MultiHolder = MultiHolder;
/**
 * Sets owner of obj (i.e. calls owner.autoDispose(obj)) unless owner is null. Returns obj.
 */
function setDisposeOwner(owner, obj) {
    if (owner) {
        owner.autoDispose(obj);
    }
    return obj;
}
exports.setDisposeOwner = setDisposeOwner;
/**
 * Helper for reporting errors during disposal. Try to report the type of the object.
 */
function _describe(obj) {
    return (obj && obj.constructor && obj.constructor.name ? obj.constructor.name : '' + obj);
}
/**
 * DisposalList is an internal class mimicking emit.Emitter. The difference is that callbacks are
 * called in reverse order, and exceptions in callbacks are reported and swallowed.
 */
class DisposalList extends emit_1.LLink {
    constructor() { super(); }
    addListener(callback, optContext) {
        const lis = new DisposeListener(callback, optContext);
        this._insertBefore(this._next, lis);
        return lis;
    }
    /**
     * Call all callbacks and dispose this object. The owner is required for better reporting of
     * errors if any callback throws.
     */
    callAndDispose(owner) {
        try {
            DisposeListener.callAll(this._next, this, owner);
        }
        finally {
            this._disposeList();
        }
    }
}
/**
 * Internal class that keeps track of one item of the DisposalList. It mimicks emit.Listener, but
 * reports and swallows erros when it calls the callbacks in the list.
 */
class DisposeListener extends emit_1.LLink {
    constructor(callback, context) {
        super();
        this.callback = callback;
        this.context = context;
    }
    static callAll(begin, end, owner) {
        while (begin !== end) {
            const lis = begin;
            try {
                lis.callback.call(lis.context);
            }
            catch (e) {
                // tslint:disable-next-line:no-console
                console.error("While disposing %s, error disposing %s: %s", _describe(owner), _describe(this), e);
            }
            begin = lis._next;
        }
    }
    dispose() {
        if (this.isDisposed()) {
            return;
        }
        this._removeNode(this);
    }
}
//# sourceMappingURL=dispose.js.map